import { useState } from "react";
import { useTranslation } from "react-i18next";
import useMainStore from "../../store/mainState";
import { useMutation, useQuery } from "@apollo/client";
import { PYBIT_WITHDRAW, GET_PAYMENT_METHODS } from "../../graphql/transaction";
import GInput from "../../components/ui/base/GInput";
import GLoader from "../../components/ui/base/GLoader";
import { ReactComponent as SuccessIcon } from "../../assets/icons/succes.svg";
import { ReactComponent as FaildIcon } from "../../assets/icons/faild.svg";
import { useNavigate } from "react-router-dom";
import { ReactComponent as BitcoinIcon } from "../../assets/icons/bitcoin.svg";
import CryptoSelect from "../../components/ui/CryptoSelect";

const CashOut = ({ setSelectedPaymentMethod }: any) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { environment } = useMainStore();
    const [paymentMethodes, setPaymentMethodes] = useState<any>();
    const { data } = useQuery(GET_PAYMENT_METHODS, {
        variables: { type: "payment", activeOnly: true, environmentId: environment?.id },
        fetchPolicy: "cache-and-network",
        skip: !environment?.id,
    });
    const [withdraw, { loading: withdrawLoading }] = useMutation(PYBIT_WITHDRAW);
    const [confirmCoin, setConfirmCoin] = useState<boolean>(false);
    const [amount, setAmount] = useState<any>(0);
    const [address, setAddress] = useState<any>(undefined);
    const [isSuccesPayment, setIsSuccessPayment] = useState(false);
    const [isPaymentDone, setIsPaymentDone] = useState(false);

    const selectedCoin = data?.paymentMethodActions?.find(
        (m: any) => String(m.id) === String(paymentMethodes)
    ) || data?.paymentMethodActions?.[0];

    const handleConfirm = async () => {
        const mainnet = paymentMethodes ? paymentMethodes : data?.paymentMethodActions?.[0]?.id;
        setPaymentMethodes(mainnet);
        setConfirmCoin(true);
    };

    const handleWithdrow = async () => {
        const payment = data?.paymentMethodActions?.find((item: any) => item.id == paymentMethodes);
        try {
            await withdraw({
                variables: {
                    address: String(address),
                    amount: String(amount),
                    coin: String(payment?.coin),
                    code: String(payment?.code),
                    chain: String(payment?.chain),
                },
            });
            setIsSuccessPayment(true);
            setIsPaymentDone(true);
        } catch (err) {
            console.log({ err });
            setIsSuccessPayment(false);
            setIsPaymentDone(true);
        }
    };

    // Step indicator
    const currentStep = isPaymentDone ? 3 : confirmCoin ? 2 : 1;

    return (
        <div className="w-full">
            {withdrawLoading && <GLoader />}

            {/* Step Progress */}
            {!isPaymentDone && (
                <div className="flex items-center gap-2 mb-5">
                    {[t('payment.selectCoin'), t('payment.enterDetails'), t('payment.done')].map((label, i) => (
                        <div key={label} className="flex items-center gap-2 flex-1">
                            <div className="flex flex-col items-center gap-1 flex-1">
                                <div
                                    className={`w-full h-1 rounded-full transition-all duration-300 ${i + 1 <= currentStep ? "bg-gradient-to-r from-[#4DBDFF] to-[#0086D3]" : "bg-white/10"
                                        }`}
                                />
                                <span className={`text-[10px] font-medium ${i + 1 <= currentStep ? "text-[#4DBDFF]" : "text-white/30"}`}>
                                    {label}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {isPaymentDone ? (
                <div className="flex flex-col items-center gap-4 py-4">
                    {isSuccesPayment ? (
                        <>
                            <div
                                className="w-16 h-16 rounded-full flex items-center justify-center"
                                style={{ background: "rgba(76, 175, 80, 0.12)", border: "1px solid rgba(76, 175, 80, 0.2)" }}
                            >
                                <SuccessIcon className="w-8 h-8" />
                            </div>
                            <h3 className="text-white text-lg font-bold">{t('payment.withdrawalSuccessful')}</h3>
                            <p className="text-white/50 text-sm text-center leading-relaxed">
                                {t('payment.withdrawalConfirmed')}
                            </p>
                            <div
                                className="flex items-center gap-2 w-full p-3 rounded-xl mt-1"
                                style={{ background: "rgba(255, 255, 255, 0.04)" }}
                            >
                                <div className="flex-1 text-xs">
                                    <span className="text-white/40">{t('payment.amountLabel')}</span>{" "}
                                    <span className="text-white font-medium">{amount} USD</span>
                                </div>
                            </div>
                            <p className="text-white/50 text-sm text-center leading-relaxed">
                                {t('payment.youWillReceive', { amount, coin: selectedCoin?.coin })}
                            </p>
                            <button
                                className="w-full text-white text-sm font-semibold py-3 rounded-xl mt-2 transition-all duration-200 hover:shadow-lg hover:shadow-[#0086D3]/25 active:scale-[0.98]"
                                style={{ background: "linear-gradient(135deg, #4DBDFF, #0086D3)" }}
                                onClick={() => navigate("/")}
                            >
                                {t('payment.backToHome')}
                            </button>
                        </>
                    ) : (
                        <>
                            <div
                                className="w-16 h-16 rounded-full flex items-center justify-center"
                                style={{ background: "rgba(217, 105, 105, 0.12)", border: "1px solid rgba(217, 105, 105, 0.2)" }}
                            >
                                <FaildIcon className="w-8 h-8" />
                            </div>
                            <h3 className="text-white text-lg font-bold">{t('payment.withdrawalFailed')}</h3>
                            <p className="text-white/50 text-sm text-center leading-relaxed">
                                {t('payment.withdrawalFailedText')}
                            </p>
                            <div className="flex gap-3 w-full mt-2">
                                <button
                                    className="flex-1 text-white text-sm font-semibold py-3 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-[#0086D3]/25 active:scale-[0.98]"
                                    style={{ background: "linear-gradient(135deg, #4DBDFF, #0086D3)" }}
                                    onClick={() => {
                                        setIsPaymentDone(false);
                                        setIsSuccessPayment(false);
                                    }}
                                >
                                    {t('payment.tryAgain')}
                                </button>
                                <button
                                    className="flex-1 text-white/60 hover:text-white text-sm font-semibold py-3 rounded-xl transition-all duration-200 hover:bg-white/[0.06] active:scale-[0.98]"
                                    style={{ border: "1px solid rgba(255, 255, 255, 0.1)" }}
                                    onClick={() => navigate("/")}
                                >
                                    {t('common.cancel')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            ) : confirmCoin ? (
                <div className="flex flex-col gap-4 w-full">
                    {/* Selected coin summary */}
                    {selectedCoin && (
                        <div
                            className="flex items-center gap-3 w-full p-3 rounded-xl"
                            style={{ background: "rgba(255, 255, 255, 0.04)" }}
                        >
                            <div className="w-9 h-9 rounded-full bg-[#4DBDFF]/10 flex items-center justify-center flex-shrink-0">
                                <BitcoinIcon className="w-5 h-5" />
                            </div>
                            <div className="flex-1">
                                <p className="text-white text-sm font-medium">{selectedCoin.coin}</p>
                                <p className="text-white/40 text-xs">{selectedCoin.chain} network</p>
                            </div>
                            <button
                                className="text-[#4DBDFF] text-xs font-medium hover:underline"
                                onClick={() => setConfirmCoin(false)}
                            >
                                {t('payment.change')}
                            </button>
                        </div>
                    )}

                    <GInput className="w-full" label={t('payment.amount')} type="number" placeholder="0.00" value={amount} onChange={(e) => setAmount(e.target.value)} />
                    <GInput className="w-full" label={t('payment.walletAddress')} type="text" placeholder={t('payment.enterDestination')} value={address} onChange={(e) => setAddress(e.target.value)} />

                    <div className="flex gap-3 mt-4">
                        <button
                            disabled={!amount || !address}
                            className="flex-1 text-white text-sm font-semibold py-3 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-[#0086D3]/25 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:shadow-none"
                            style={{ background: "linear-gradient(135deg, #4DBDFF, #0086D3)" }}
                            onClick={handleWithdrow}
                        >
                            {t('payment.confirmWithdrawal')}
                        </button>
                        <button
                            className="px-5 text-white/60 hover:text-white text-sm font-semibold py-3 rounded-xl transition-all duration-200 hover:bg-white/[0.06] active:scale-[0.98]"
                            style={{ border: "1px solid rgba(255, 255, 255, 0.1)" }}
                            onClick={() => setConfirmCoin(false)}
                        >
                            {t('payment.back')}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex flex-col gap-4 w-full">
                    <p className="text-white/50 text-xs font-medium">{t('payment.selectCrypto')}</p>
                    <CryptoSelect
                        options={data?.paymentMethodActions || []}
                        value={paymentMethodes}
                        onChange={(val) => setPaymentMethodes(val)}
                        placeholder={t('payment.selectCrypto')}
                    />

                    <button
                        className="w-full text-white text-sm font-semibold py-3 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-[#0086D3]/25 active:scale-[0.98] mt-2"
                        style={{ background: "linear-gradient(135deg, #4DBDFF, #0086D3)" }}
                        onClick={handleConfirm}
                    >
                        {t('payment.continue')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default CashOut;
